import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Box,
  Card,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CommuteIcon from '@mui/icons-material/Commute';

const ApprovalRecords = () => {
  const [selectedType, setSelectedType] = useState('leave');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter & Pagination States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchRecords = async (type) => {
    setLoading(true);
    try {
      const endpoint =
        type === 'leave'
          ? 'https://gannondunkerley.in/leave/'
          : 'https://gannondunkerley.in/ODRecord/';

      const res = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      // Sort newest first by startDate
      const sorted = (Array.isArray(res.data) ? res.data : []).sort(
        (a, b) => new Date(b.startDate) - new Date(a.startDate)
      );

      setRecords(sorted);
    } catch (err) {
      console.error('Error fetching records:', err);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(selectedType);
    setPage(0);
  }, [selectedType]);

  // Filter logic (Search + Status)
  const filteredRecords = useMemo(() => {
    return records.filter((rec) => {
      const matchesSearch =
        (rec.name && rec.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (rec.empCode && String(rec.empCode).toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus =
        statusFilter === 'ALL' ||
        (rec.approvalStatus &&
          rec.approvalStatus.toUpperCase() === statusFilter.toUpperCase());

      return matchesSearch && matchesStatus;
    });
  }, [records, searchTerm, statusFilter]);

  // Export to Excel function
  const handleExportExcel = () => {
    if (filteredRecords.length === 0) return;

    const exportData = filteredRecords.map((rec) => {
      const common = {
        'Employee Name': rec.name || 'N/A',
        'Employee Code': rec.empCode || 'N/A',
        'From Date': rec.startDate ? new Date(rec.startDate).toLocaleDateString() : 'N/A',
        'To Date': rec.endDate ? new Date(rec.endDate).toLocaleDateString() : 'N/A',
      };

      if (selectedType === 'leave') {
        return {
          ...common,
          'Requested Days': rec.requestedDays ?? 0,
          'Deducted Days': rec.deductedDays ?? 0,
          'Remaining Un-deducted': rec.remainingDaysNotDeducted ?? 0,
          'Reason': rec.reason || '',
          'Status': rec.approvalStatus || 'Pending',
          'Approver': rec.approver || 'N/A',
        };
      } else {
        return {
          ...common,
          'On Duty Location': rec.location || 'N/A',
          'Reason': rec.reason || '',
          'Status': rec.approvalStatus || 'Pending',
          'Approver': rec.approver || 'N/A',
        };
      }
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, selectedType.toUpperCase());

    const fileName = `${selectedType.toUpperCase()}_Records_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const getStatusChip = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'APPROVED') {
      return (
        <Chip
          label="Approved"
          size="small"
          sx={{ bgcolor: '#e6f4ea', color: '#137333', fontWeight: 600 }}
        />
      );
    }
    if (s === 'REJECTED') {
      return (
        <Chip
          label="Rejected"
          size="small"
          sx={{ bgcolor: '#fce8e6', color: '#c5221f', fontWeight: 600 }}
        />
      );
    }
    return (
      <Chip
        label="Pending"
        size="small"
        sx={{ bgcolor: '#fef7e0', color: '#b06000', fontWeight: 600 }}
      />
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Card
        sx={{
          maxWidth: 1300,
          mx: 'auto',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        }}
      >
        {/* Header Section */}
        <Box sx={{ p: 3, bgcolor: '#ffffff' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                HR Review & Records
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                Review and audit historical leave and on-duty requests
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportExcel}
              disabled={filteredRecords.length === 0}
              sx={{
                bgcolor: '#0284c7',
                '&:hover': { bgcolor: '#0369a1' },
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 2.5,
              }}
            >
              Export Excel ({filteredRecords.length})
            </Button>
          </Stack>
        </Box>

        <Divider />

        {/* Navigation Tabs */}
        <Box sx={{ px: 3, bgcolor: '#ffffff' }}>
          <Tabs
            value={selectedType}
            onChange={(e, val) => setSelectedType(val)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab
              value="leave"
              icon={<EventNoteIcon fontSize="small" />}
              iconPosition="start"
              label="Leave Applications"
              sx={{ textTransform: 'none', fontWeight: 600, py: 2 }}
            />
            <Tab
              value="od"
              icon={<CommuteIcon fontSize="small" />}
              iconPosition="start"
              label="On Duty (OD) Logs"
              sx={{ textTransform: 'none', fontWeight: 600, py: 2 }}
            />
          </Tabs>
        </Box>

        <Divider />

        {/* Toolbar: Search and Filter */}
        <Box sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Search by Employee Name or Code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1, bgcolor: '#ffffff', borderRadius: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 160, bgcolor: '#ffffff', borderRadius: 1 }}
            >
              <MenuItem value="ALL">All Statuses</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
            </TextField>
          </Stack>
        </Box>

        {/* Table Area */}
        {loading ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <CircularProgress size={40} thickness={4} />
            <Typography variant="body2" sx={{ color: '#64748b', mt: 2 }}>
              Loading review records...
            </Typography>
          </Box>
        ) : filteredRecords.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: '#475569', fontWeight: 600 }}>
              No records found
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
              Try adjusting your search criteria or status filter.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }}>Period</TableCell>
                    {selectedType === 'leave' ? (
                      <>
                        <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }}>
                          Days
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }}>
                          Deducted
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }}>
                          Remaining
                        </TableCell>
                      </>
                    ) : (
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }}>Location</TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }}>Reason</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }}>Approver</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRecords
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((rec) => (
                      <TableRow key={rec._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>{rec.name}</TableCell>
                        <TableCell sx={{ color: '#64748b', fontFamily: 'monospace' }}>{rec.empCode}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(rec.startDate).toLocaleDateString()} &rarr;{' '}
                          {new Date(rec.endDate).toLocaleDateString()}
                        </TableCell>

                        {selectedType === 'leave' ? (
                          <>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>{rec.requestedDays ?? 0}</TableCell>
                            <TableCell align="center" sx={{ color: '#0369a1' }}>{rec.deductedDays ?? 0}</TableCell>
                            <TableCell align="center" sx={{ color: '#e11d48' }}>
                              {rec.remainingDaysNotDeducted ?? 0}
                            </TableCell>
                          </>
                        ) : (
                          <TableCell sx={{ color: '#475569' }}>{rec.location || 'N/A'}</TableCell>
                        )}

                        <TableCell sx={{ maxWidth: 220, color: '#475569' }} title={rec.reason}>
                          <Typography variant="body2" noWrap>
                            {rec.reason || '—'}
                          </Typography>
                        </TableCell>

                        <TableCell align="center">{getStatusChip(rec.approvalStatus)}</TableCell>
                        <TableCell sx={{ color: '#64748b' }}>{rec.approver || '—'}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredRecords.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Card>
    </Box>
  );
};

export default ApprovalRecords;